import { FormControl, FormGroup } from "@angular/forms";
import { EntityFormModel } from "@kernel/form/classes/entity-form-model";
import { TControlsValue } from "@kernel/form/types/form-controls-utils.types";
import { IPropertyFiltersInput } from "../interfaces/property-filters-input.interface";
import { IPropertyFiltersControls } from "../interfaces/property-filters-controls.interface";
import { filterOptions, TFilterOptions } from "@kernel/listing/filter/enums/filter-options";
import { FilterInput } from "@kernel/graphql/models";
import { TPropertyFilters } from "@app/core/filtering/interfaces/property-filters.interface";
import {
  isPropertyMatchOptionActive,
  PROPERTY_MATCH_OPTIONS,
  removePropertyMatchOption,
  TPropertyMatchOption,
} from "@app/core/filtering/helpers/property-match.helper";
import { inject } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { capitalToCamel } from "@app/shared/general/helpers/capital-to-camel.helper";

export class PropertyFiltersFormModel extends EntityFormModel<IPropertyFiltersInput, TPropertyFilters, IPropertyFiltersControls> {
  translateService = inject(TranslateService);

  /**
   * Maps an API/entity filter object into a form-compatible value shape.
   * Ensures all expected form controls receive values (or undefined).
   *
   * @param entity - Optional filters entity coming from backend or storage.
   * @returns Form-ready controls value object.
   */
  entityToFormValue(entity?: IPropertyFiltersInput | null): TControlsValue<IPropertyFiltersControls> {
    return {
      match: entity?.match,
      city: entity?.city,
      propertyType: entity?.propertyType,
      features: entity?.features,
      minBudget: entity?.minBudget,
      maxBudget: entity?.maxBudget,
      rooms: entity?.rooms,
      livingSpace: entity?.livingSpace,
    };
  }

  /**
   * Converts form values into DTO filter inputs used by the backend/query layer.
   * Empty, null, or invalid values are excluded automatically.
   *
   * @param formValue - Current form control values.
   * @returns Array of filter inputs ready for API consumption.
   */
  formValueToDtoInput(formValue: TControlsValue<IPropertyFiltersControls>): TPropertyFilters {
    /**
     * Helper for conditionally creating filter inputs.
     * Returns `undefined` if the value is empty or invalid, so it will not be sent to the backend.
     */
    const add = (field: string, op: TFilterOptions, value: unknown) => {
      return value && (!Array.isArray(value) || value.length) ? new FilterInput(field, op, String(value)) : undefined;
    };

    return [
      // Budget range
      add("budget", filterOptions.GREATER_THAN_OR_EQ.key, formValue.minBudget),
      add("budget", filterOptions.LESS_THAN_OR_EQ.key, formValue.maxBudget),

      // Rooms range
      add("space.numberOfRooms", filterOptions.GREATER_THAN_OR_EQ.key, formValue.rooms?.min),
      add("space.numberOfRooms", filterOptions.LESS_THAN_OR_EQ.key, formValue.rooms?.max),

      // Living space range
      add("space.livingSpace", filterOptions.GREATER_THAN_OR_EQ.key, formValue.livingSpace?.min),
      add("space.livingSpace", filterOptions.LESS_THAN_OR_EQ.key, formValue.livingSpace?.max),

      // Multi-select filters
      add("features", filterOptions.IN.key, formValue.features?.join(",")),
      add("propertyType", filterOptions.IN.key, formValue.propertyType?.join(",")),
      add("city", filterOptions.IN.key, formValue.city?.join(",")),

      // Match bitmask filter
      add("match", filterOptions.IS.key, formValue.match),
    ].filter(Boolean) as TPropertyFilters;
  }

  /**
   * Creates the strongly-typed Angular form group for property filters.
   * Applies safe defaults for array-based controls.
   *
   * @param initialValue - Initial values to hydrate the form.
   * @returns Fully constructed FormGroup instance.
   */
  protected createFormGroup(initialValue: TControlsValue<IPropertyFiltersControls>): FormGroup<IPropertyFiltersControls> {
    return new FormGroup<IPropertyFiltersControls>({
      match: new FormControl(initialValue.match),
      city: new FormControl(initialValue.city ?? []),
      propertyType: new FormControl(initialValue.propertyType ?? []),
      features: new FormControl(initialValue.features ?? []),
      minBudget: new FormControl(initialValue.minBudget),
      maxBudget: new FormControl(initialValue.maxBudget),
      rooms: new FormControl(initialValue.rooms),
      livingSpace: new FormControl(initialValue.livingSpace),
    });
  }

  /**
   * Converts form values into a UI-friendly preview object.
   * Values are formatted as strings or booleans and may be undefined if empty.
   *
   * @param formValue - Current form control values.
   * @returns Key-value object used for displaying active filters.
   */
  formValueToFiltersPreview(formValue: TControlsValue<IPropertyFiltersControls>): Record<string, string | undefined> {
    return {
      bestMatch: isPropertyMatchOptionActive(formValue.match ?? 0, PROPERTY_MATCH_OPTIONS.BEST_MATCH)
        ? this.translateService.instant("COMMON.yes")
        : undefined,
      mutualInterest: isPropertyMatchOptionActive(formValue.match ?? 0, PROPERTY_MATCH_OPTIONS.MUTUAL_INTEREST)
        ? this.translateService.instant("COMMON.yes")
        : undefined,
      likedMe: isPropertyMatchOptionActive(formValue.match ?? 0, PROPERTY_MATCH_OPTIONS.LIKED_ME)
        ? this.translateService.instant("COMMON.yes")
        : undefined,
      city: formValue.city?.join(", "),
      propertyType: formValue.propertyType?.map(f => this.translateService.instant(`homeTypes.${capitalToCamel(f)}`))?.join(", "),
      features: formValue.features?.map(f => this.translateService.instant(`homeFeatures.${capitalToCamel(f)}`))?.join(", "),
      budget: this.formatRangeValuePreview({ min: formValue.minBudget, max: formValue.maxBudget }),
      rooms: this.formatRangeValuePreview(formValue.rooms),
      livingSpace: this.formatRangeValuePreview(formValue.livingSpace),
    };
  }

  /**
   * Clears a filter input based on its preview key.
   *
   * This method maps UI preview keys to their corresponding form controls
   * or match option bits and resets/removes them accordingly.
   *
   * @param key - The preview key identifying which filter should be cleared.
   *
   * @example
   * ```ts
   * clearInputByPreviewKey('bestMatch'); // Removes BEST_MATCH option
   * clearInputByPreviewKey('budget');    // Resets minBudget and maxBudget
   * clearInputByPreviewKey('city');      // Resets the "city" form control
   * ```
   */
  clearInputByPreviewKey(key: string): void {
    switch (key) {
      case "bestMatch":
        this.removeMatchOption(PROPERTY_MATCH_OPTIONS.BEST_MATCH);
        break;
      case "mutualInterest":
        this.removeMatchOption(PROPERTY_MATCH_OPTIONS.MUTUAL_INTEREST);
        break;
      case "likedMe":
        this.removeMatchOption(PROPERTY_MATCH_OPTIONS.LIKED_ME);
        break;
      case "budget":
        this.getControl("minBudget").reset();
        this.getControl("maxBudget").reset();
        break;
      default:
        this.getControl(key as keyof IPropertyFiltersControls).reset();
    }
  }

  /**
   * Removes (clears) a specific property match option from the match bitmask control.
   *
   * If the option bit is already cleared, the value remains unchanged.
   *
   * @param optionBit - The match option to remove (one of `TPropertyMatchOption` / `PROPERTY_MATCH_OPTIONS` values).
   */
  removeMatchOption(optionBit: TPropertyMatchOption): void {
    this.getControl("match").setValue(removePropertyMatchOption(this.getControl("match").value ?? 0, optionBit));
  }

  /**
   * Formats a numeric range into a human-readable preview string.
   *
   * Examples:
   *  - { min: 10, max: 50 } → "10 - 50"
   *  - { min: 20 } → "> 20"
   *  - { max: 100 } → "< 100"
   *
   * @param value - Range object with optional min/max.
   * @returns Formatted range string or undefined if empty.
   */
  private formatRangeValuePreview(value?: { min?: number | null; max?: number | null } | null) {
    if (value?.min && value?.max) {
      return `${value?.min} - ${value?.max}`;
    }
    return value?.min ? `> ${value?.min}` : value?.max ? `< ${value?.max}` : undefined;
  }

  /**
   * Returns the current filters preview with all empty values removed.
   * Useful for compact UI display or query badges.
   *
   * @returns Object containing only active filters.
   */
  getCurrentFiltersPreview(): Record<string, string> {
    return Object.fromEntries(Object.entries(this.formValueToFiltersPreview(this.getCurrentFormValue())).filter(([, v]) => Boolean(v))) as Record<
      string,
      string
    >;
  }

  /**
   * Adds a city to the `city` multi-select control.
   *
   * @param location - City name to add.
   */
  addCity(location: string): void {
    this.getControl("city").setValue([...(this.getCurrentFormValue().city ?? []), location]);
  }

  /**
   * Removes a city from the `city` control by index.
   *
   * @param index - Index of the city to remove.
   */
  removeCity(index: number): void {
    this.getControl("city").setValue((this.getCurrentFormValue().city ?? []).filter((_, x) => x !== index));
  }
}
